import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  updateUserAvatar,
  buildUserAvatarUrl,
  updateUserAboutMe,
  updateUserProfileBasics,
  updateUserHero,
  removeUserHero,
  buildUserHeroUrl,
  listUserProfileViews,
} from '../../services/profileApi';
import { http } from '../../lib/api/http';
import { refreshSessionUser } from '../../services/sessionUser';
import {
  defaultCompanyValues,
  fetchCompanyContext,
  updateCompanyProfile,
  listCompanyProfileViews,
} from '../../services/companyApi';
import {
  listCompanyJoinRequests,
  approveCompanyJoinRequest,
  rejectCompanyJoinRequest,
} from '../../services/companyJoinRequests';
import { listPublicationViewsByCompany } from '../../services/publicationsApi';

const LAST_PLAN_CHECKOUT_KEY = 'flotte:lastPlanCheckout';
const LAST_PLAN_APPLIED_KEY = 'flotte:lastPlanApplied';
const LAST_CREDITS_CHECKOUT_KEY = 'flotte:lastCreditsCheckout';
const LAST_CREDITS_APPLIED_KEY = 'flotte:lastCreditsApplied';

const safeNumber = value => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const computeNextRenewalDate = (timestamp, billingCycle) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  if (billingCycle === 'yearly') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }
  return date.toISOString();
};

export default function useDashboardState(currentUser) {
  const userRole = currentUser?.role ?? 'user';

  const [section, setSection] = useState('feed');
  const menuRef = useRef(null);

  // --- Empresa: informacion de la compania ---
  const [empresaId, setEmpresaId] = useState('');
  const [empresaValues, setEmpresaValues] = useState({ ...defaultCompanyValues });
  const [empresaUsers, setEmpresaUsers] = useState([]);
  const [empresaProfileViews, setEmpresaProfileViews] = useState([]);
  const [empresaProfileViewsTotal, setEmpresaProfileViewsTotal] = useState(0);
  const [empresaUserRequests, setEmpresaUserRequests] = useState([]);
  const [empresaLoading, setEmpresaLoading] = useState(false);
  const [empresaUploading, setEmpresaUploading] = useState(false);
  const [empresaError, setEmpresaError] = useState('');
  const [canEditCompany, setCanEditCompany] = useState(false);
  const [canManageCompanyUsers, setCanManageCompanyUsers] = useState(false);
  const [userProfileViews, setUserProfileViews] = useState([]);
  const [userProfileViewsTotal, setUserProfileViewsTotal] = useState(0);
  const [publicationViewDetails, setPublicationViewDetails] = useState([]);
  const [publicationViewTotal, setPublicationViewTotal] = useState(0);
  const logoPreviewRef = useRef('');
  const heroPreviewRef = useRef('');

  const syncUserCompanyMembership = useCallback(
    async targetCompanyId => {
      if (!currentUser?.id || !targetCompanyId) return;
      const currentCompanyId = (() => {
        if (!currentUser?.company) return '';
        if (typeof currentUser.company === 'string') {
          return currentUser.company.trim();
        }
        if (Array.isArray(currentUser.company) && currentUser.company.length === 1) {
          const candidate = currentUser.company[0];
          if (typeof candidate === 'string') {
            return candidate.trim();
          }
          if (candidate && typeof candidate.id === 'string') {
            return candidate.id.trim();
          }
        }
        if (typeof currentUser.company === 'object') {
          if (typeof currentUser.company.id === 'string') {
            return currentUser.company.id.trim();
          }
          if (
            typeof currentUser.company.id === 'number' &&
            Number.isFinite(currentUser.company.id)
          ) {
            return String(currentUser.company.id);
          }
        }
        return '';
      })();
      if (currentCompanyId === targetCompanyId) {
        return;
      }
      try {
        await http.patch(`/users/${encodeURIComponent(currentUser.id)}`, {
          company_id: targetCompanyId,
        });
        await refreshSessionUser().catch(() => {});
      } catch (error) {
        console.warn('No se pudo sincronizar la empresa del usuario actual.', error);
      }
    },
    [currentUser?.id, currentUser?.company]
  );

  const applyPlanSelection = useCallback(detail => {
    if (!detail) return;
    setEmpresaValues(prev => {
      const nextValues = { ...prev };
      let didChange = false;

      const snapshot = detail.planSnapshot || null;
      const resolvedSlug = String(snapshot?.slug || detail.planId || '')
        .trim()
        .toLowerCase();
      const prevSlug = String(prev.membershipPlan || '').trim().toLowerCase();
      if (resolvedSlug && resolvedSlug !== prevSlug) {
        nextValues.membershipPlan = resolvedSlug;
        didChange = true;
      }

      if (snapshot && Number.isFinite(Number(snapshot.publicationsLimit))) {
        const nextPublicationLimit = Math.max(0, Number(snapshot.publicationsLimit));
        if (nextPublicationLimit !== prev.publicationLimit) {
          nextValues.publicationLimit = nextPublicationLimit;
          didChange = true;
        }
      }

      if (snapshot && Number.isFinite(Number(snapshot.teamSeats))) {
        const nextUserLimit = Math.max(0, Number(snapshot.teamSeats));
        if (nextUserLimit !== prev.userLimit) {
          nextValues.userLimit = nextUserLimit;
          didChange = true;
        }
      }

      if (!nextValues.canPublish) {
        nextValues.canPublish = true;
        didChange = true;
      }

      const renewalAt = computeNextRenewalDate(detail.timestamp, detail.billingCycle);
      if (renewalAt && renewalAt !== prev.membershipRenewsAt) {
        nextValues.membershipRenewsAt = renewalAt;
        didChange = true;
      }

      if (!didChange) {
        return prev;
      }
      return nextValues;
    });
    if (typeof window !== 'undefined') {
      try {
        const appliedTimestamp = detail?.timestamp || String(Date.now());
        window.sessionStorage.setItem(LAST_PLAN_APPLIED_KEY, appliedTimestamp);
      } catch (storageError) {
        console.warn('No se pudo registrar la aplicacion del plan simulado.', storageError);
      }
    }
  }, []);

  const applyCreditsRecharge = useCallback(detail => {
    if (!detail) return;
    const ppcIncrement = Math.max(0, safeNumber(detail.ppcCredits));
    const leadIncrement = Math.max(0, safeNumber(detail.leadCredits));
    if (!ppcIncrement && !leadIncrement) return;
    setEmpresaValues(prev => {
      const currentPpc = safeNumber(prev.ppcCredits);
      const currentLead = safeNumber(prev.leadCredits);
      const nextPpc = currentPpc + ppcIncrement;
      const nextLead = currentLead + leadIncrement;
      if (nextPpc === currentPpc && nextLead === currentLead) {
        return prev;
      }
      return {
        ...prev,
        ppcCredits: nextPpc,
        leadCredits: nextLead,
      };
    });
    if (typeof window !== 'undefined') {
      try {
        const appliedTimestamp = detail?.timestamp || String(Date.now());
        window.sessionStorage.setItem(LAST_CREDITS_APPLIED_KEY, appliedTimestamp);
      } catch (storageError) {
        console.warn('No se pudo registrar la recarga simulada.', storageError);
      }
    }
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadCompany = async () => {
      if (!currentUser) {
        if (!ignore) {
          setEmpresaId('');
          setEmpresaValues({ ...defaultCompanyValues });
          setEmpresaUsers([]);
          setEmpresaProfileViews([]);
          setEmpresaProfileViewsTotal(0);
          setEmpresaUserRequests([]);
          setCanEditCompany(false);
          setCanManageCompanyUsers(false);
          setUserProfileViews([]);
          setUserProfileViewsTotal(0);
          setPublicationViewDetails([]);
          setPublicationViewTotal(0);
        }
        return;
      }

      setEmpresaLoading(true);
      setEmpresaError('');
      try {
        const ctx = await fetchCompanyContext(currentUser);
        if (ignore) return;
        setEmpresaId(ctx.companyId);
        if (logoPreviewRef.current) {
          URL.revokeObjectURL(logoPreviewRef.current);
          logoPreviewRef.current = '';
        }
        if (heroPreviewRef.current) {
          URL.revokeObjectURL(heroPreviewRef.current);
          heroPreviewRef.current = '';
        }
        setEmpresaValues(ctx.values);
        setEmpresaUsers(ctx.users);
        setEmpresaUserRequests(ctx.userRequests || []);
        if (ctx.companyId) {
          syncUserCompanyMembership(ctx.companyId);
        }
        setCanEditCompany(ctx.canEditCompany);
        setCanManageCompanyUsers(ctx.canManageUsers);
        if (ctx.companyId) {
          try {
            const { items, total } = await listCompanyProfileViews(ctx.companyId, { limit: 25 });
            if (!ignore) {
              setEmpresaProfileViews(items);
              setEmpresaProfileViewsTotal(total);
            }
          } catch (viewError) {
            if (!ignore) {
              setEmpresaProfileViews([]);
              setEmpresaProfileViewsTotal(0);
            }
          }

          try {
            const { items, total } = await listPublicationViewsByCompany(ctx.companyId, {
              limit: 60,
            });
            if (!ignore) {
              setPublicationViewDetails(items);
              setPublicationViewTotal(total);
            }
          } catch (publicationError) {
            if (!ignore) {
              setPublicationViewDetails([]);
              setPublicationViewTotal(0);
            }
          }
        } else if (!ignore) {
          setEmpresaProfileViews([]);
          setEmpresaProfileViewsTotal(0);
          setPublicationViewDetails([]);
          setPublicationViewTotal(0);
        }

        if (currentUser?.id) {
          try {
            const { items, total } = await listUserProfileViews(currentUser.id, { limit: 40 });
            if (!ignore) {
              setUserProfileViews(items);
              setUserProfileViewsTotal(total);
            }
          } catch (userViewError) {
            if (!ignore) {
              setUserProfileViews([]);
              setUserProfileViewsTotal(0);
            }
          }
        } else if (!ignore) {
          setUserProfileViews([]);
          setUserProfileViewsTotal(0);
        }
      } catch (error) {
        if (!ignore) {
          setEmpresaError(
            error?.message || 'No se pudo cargar la informacion de la empresa.'
          );
        }
      } finally {
        if (!ignore) {
          setEmpresaLoading(false);
        }
      }
    };

    loadCompany();
    return () => {
      ignore = true;
      if (logoPreviewRef.current) {
        URL.revokeObjectURL(logoPreviewRef.current);
        logoPreviewRef.current = '';
      }
      if (heroPreviewRef.current) {
        URL.revokeObjectURL(heroPreviewRef.current);
        heroPreviewRef.current = '';
      }
      setEmpresaUserRequests([]);
      setEmpresaProfileViews([]);
      setEmpresaProfileViewsTotal(0);
      setUserProfileViews([]);
      setUserProfileViewsTotal(0);
      setPublicationViewDetails([]);
      setPublicationViewTotal(0);
    };
  }, [currentUser?.id, currentUser?.updated, currentUser, syncUserCompanyMembership]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePlanUpdated = event => {
      applyPlanSelection(event?.detail || null);
    };

    const handleCreditsUpdated = event => {
      applyCreditsRecharge(event?.detail || null);
    };

    window.addEventListener('flotte:plan-updated', handlePlanUpdated);
    window.addEventListener('flotte:credits-updated', handleCreditsUpdated);

    return () => {
      window.removeEventListener('flotte:plan-updated', handlePlanUpdated);
      window.removeEventListener('flotte:credits-updated', handleCreditsUpdated);
    };
  }, [applyPlanSelection, applyCreditsRecharge]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const applyPendingCheckouts = () => {
      try {
        const rawPlan = window.localStorage.getItem(LAST_PLAN_CHECKOUT_KEY);
        const appliedPlan = window.sessionStorage.getItem(LAST_PLAN_APPLIED_KEY);
        if (rawPlan) {
          const payload = JSON.parse(rawPlan);
          if (!payload?.timestamp || payload.timestamp !== appliedPlan) {
            applyPlanSelection(payload);
          }
        }
      } catch (planError) {
        console.warn('No se pudo sincronizar el ultimo plan simulado.', planError);
      }

      try {
        const rawCredits = window.localStorage.getItem(LAST_CREDITS_CHECKOUT_KEY);
        const appliedCredits = window.sessionStorage.getItem(LAST_CREDITS_APPLIED_KEY);
        if (rawCredits) {
          const payload = JSON.parse(rawCredits);
          if (!payload?.timestamp || payload.timestamp !== appliedCredits) {
            applyCreditsRecharge(payload);
          }
        }
      } catch (creditsError) {
        console.warn('No se pudo sincronizar la recarga simulada.', creditsError);
      }
    };

    applyPendingCheckouts();

    const handleVisibility = () => {
      if (document?.hidden) return;
      applyPendingCheckouts();
    };

    window.addEventListener('focus', applyPendingCheckouts);
    document?.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', applyPendingCheckouts);
      document?.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [applyPlanSelection, applyCreditsRecharge]);

  const handleEmpresaFieldChange = (field, value) => {
    setEmpresaValues(prev => {
      const next = { ...prev };

      if (field === 'logoFile') {
        if (logoPreviewRef.current) {
          URL.revokeObjectURL(logoPreviewRef.current);
          logoPreviewRef.current = '';
        }

        if (value instanceof File) {
          const preview = URL.createObjectURL(value);
          logoPreviewRef.current = preview;
          next.logoFile = value;
          next.logoPreview = preview;
        } else {
          next.logoFile = null;
          next.logoPreview = '';
        }
        return next;
      }

      if (field === 'heroFile') {
        if (heroPreviewRef.current) {
          URL.revokeObjectURL(heroPreviewRef.current);
          heroPreviewRef.current = '';
        }

        if (value instanceof File) {
          const preview = URL.createObjectURL(value);
          heroPreviewRef.current = preview;
          next.heroFile = value;
          next.heroPreview = preview;
        } else {
          next.heroFile = null;
          next.heroPreview = '';
        }
        return next;
      }

      if (field === 'publicationLimit' || field === 'userLimit') {
        const numeric = Number(value);
        next[field] = Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
        return next;
      }

      if (field === 'canPublish' || field === 'isVerified') {
        next[field] = Boolean(value);
        return next;
      }

      next[field] = value;
      return next;
    });
  };

  const handleEmpresaSubmit = async () => {
    if (!empresaId || !canEditCompany) return;
    setEmpresaUploading(true);
    setEmpresaError('');
    try {
      const updated = await updateCompanyProfile(empresaId, empresaValues);
      setEmpresaValues(prev => ({
        ...prev,
        ...updated,
        logoFile: null,
        logoPreview: '',
        heroFile: null,
        heroPreview: '',
      }));
      if (logoPreviewRef.current) {
        URL.revokeObjectURL(logoPreviewRef.current);
        logoPreviewRef.current = '';
      }
      if (heroPreviewRef.current) {
        URL.revokeObjectURL(heroPreviewRef.current);
        heroPreviewRef.current = '';
      }
    } catch (error) {
      setEmpresaError(error?.message || 'No se pudo guardar la empresa.');
    } finally {
      setEmpresaUploading(false);
    }
  };

  // Perfil: datos del usuario actual
  const [profileUserRecord, setProfileUserRecord] = useState(currentUser || null);
  const [profileName, setProfileName] = useState(
    currentUser?.name || currentUser?.username || currentUser?.email || ''
  );
  const [profileNameBaseline, setProfileNameBaseline] = useState(
    currentUser?.name || currentUser?.username || currentUser?.email || ''
  );
  const [profilePhone, setProfilePhone] = useState(currentUser?.phoneNumber || '');
  const [profilePhoneBaseline, setProfilePhoneBaseline] = useState(
    currentUser?.phoneNumber || ''
  );
  const [profileLocation, setProfileLocation] = useState(currentUser?.baseOperacion || '');
  const [profileLocationBaseline, setProfileLocationBaseline] = useState(
    currentUser?.baseOperacion || ''
  );
  const [profileAbout, setProfileAbout] = useState(
    currentUser?.aboutMe ?? currentUser?.about_me ?? ''
  );
  const [profileAboutBaseline, setProfileAboutBaseline] = useState(
    currentUser?.aboutMe ?? currentUser?.about_me ?? ''
  );
  const [savingAbout, setSavingAbout] = useState(false);
  const [profileAboutError, setProfileAboutError] = useState('');
  const [profileAboutSuccess, setProfileAboutSuccess] = useState('');
  const [savingBasics, setSavingBasics] = useState(false);
  const [profileBasicsError, setProfileBasicsError] = useState('');
  const [profileBasicsSuccess, setProfileBasicsSuccess] = useState('');

  const refreshUserRecord = useCallback(async () => {
    if (!currentUser?.id) return null;
    try {
      const response = await http.get(`/users/${encodeURIComponent(currentUser.id)}`);
      const record = response.data || null;
      setProfileUserRecord(record);
      return record;
    } catch {
      return null;
    }
  }, [currentUser?.id]);

  const [uploadingHero, setUploadingHero] = useState(false);
  const [heroError, setHeroError] = useState('');
  const [heroPreview, setHeroPreview] = useState('');
  const profileHeroPreviewRef = useRef('');

  const clearHeroPreview = () => {
    if (profileHeroPreviewRef.current) {
      URL.revokeObjectURL(profileHeroPreviewRef.current);
      profileHeroPreviewRef.current = '';
    }
    setHeroPreview('');
  };

  const mapUserForTable = useCallback(user => {
    if (!user) return null;
    return {
      id: user.id,
      displayName: user.name || user.email || 'Usuario sin nombre',
      email: user.email || '',
      role: user.role || 'user',
    };
  }, []);

  const refreshCompanyJoinRequests = useCallback(async () => {
    if (!empresaId) {
      setEmpresaUserRequests([]);
      return;
    }
    try {
      const list = await listCompanyJoinRequests(empresaId, { includeResolved: true });
      setEmpresaUserRequests(list);
    } catch (error) {
      if (![401, 403, 404].includes(error?.status)) {
        console.warn('No se pudieron cargar las solicitudes de acceso.', error);
      }
    }
  }, [empresaId]);

  const handleApproveJoinRequest = useCallback(
    async request => {
      if (!request || !canManageCompanyUsers) return;
      try {
        setEmpresaError('');
        const { user: updatedUser } = await approveCompanyJoinRequest(request, {
          approverId: currentUser?.id,
        });
        if (updatedUser) {
          const mapped = mapUserForTable(updatedUser);
          if (mapped) {
            setEmpresaUsers(prev => {
              if (prev.some(item => item.id === mapped.id)) {
                return prev;
              }
              return [...prev, mapped];
            });
          }
        }
      } catch (error) {
        setEmpresaError(error?.message || 'No se pudo aprobar la solicitud.');
      } finally {
        await refreshCompanyJoinRequests();
      }
    },
    [canManageCompanyUsers, currentUser?.id, mapUserForTable, refreshCompanyJoinRequests]
  );

  const handleRejectJoinRequest = useCallback(
    async request => {
      if (!request || !canManageCompanyUsers) return;
      try {
        setEmpresaError('');
        await rejectCompanyJoinRequest(request, { approverId: currentUser?.id });
      } catch (error) {
        setEmpresaError(error?.message || 'No se pudo rechazar la solicitud.');
      } finally {
        await refreshCompanyJoinRequests();
      }
    },
    [canManageCompanyUsers, currentUser?.id, refreshCompanyJoinRequests]
  );

  const handleHeroSelect = async file => {
    setHeroError('');
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setHeroError('Selecciona una imagen valida.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setHeroError('La imagen debe pesar maximo 10 MB.');
      return;
    }

    if (profileHeroPreviewRef.current) {
      URL.revokeObjectURL(profileHeroPreviewRef.current);
      profileHeroPreviewRef.current = '';
    }
    const preview = URL.createObjectURL(file);
    profileHeroPreviewRef.current = preview;
    setHeroPreview(preview);

    setUploadingHero(true);
    try {
      await updateUserHero(file);
      await refreshUserRecord();
    } catch (error) {
      setHeroError(error?.message || 'No se pudo actualizar la portada.');
      clearHeroPreview();
    } finally {
      setUploadingHero(false);
    }
  };

  const handleHeroRemove = async () => {
    setHeroError('');
    setUploadingHero(true);
    try {
      await removeUserHero();
      clearHeroPreview();
      await refreshUserRecord();
    } catch (error) {
      setHeroError(error?.message || 'No se pudo eliminar la portada.');
    } finally {
      setUploadingHero(false);
    }
  };

  useEffect(() => {
    return () => {
      clearHeroPreview();
    };
  }, []);

  useEffect(() => {
    const activeHero = profileUserRecord?.profileHero || currentUser?.profileHero;
    if (!activeHero) {
      if (profileHeroPreviewRef.current) {
        clearHeroPreview();
      }
      return;
    }
    if (!profileHeroPreviewRef.current) return;
    clearHeroPreview();
  }, [profileUserRecord?.profileHero, currentUser?.profileHero]);

  const profileHeroUrl = useMemo(() => {
    if (heroPreview) return heroPreview;
    return buildUserHeroUrl(profileUserRecord || currentUser, '1200x400');
  }, [heroPreview, profileUserRecord, currentUser]);

  useEffect(() => {
    if (!currentUser?.id) {
      setProfileUserRecord(null);
      setProfileName('');
      setProfileNameBaseline('');
      setProfilePhone('');
      setProfilePhoneBaseline('');
      setProfileLocation('');
      setProfileLocationBaseline('');
      setProfileAbout('');
      setProfileAboutBaseline('');
      setProfileAboutError('');
      setProfileAboutSuccess('');
      setProfileBasicsError('');
      setProfileBasicsSuccess('');
      setSavingBasics(false);
      setHeroError('');
      setUploadingHero(false);
      clearHeroPreview();
      return;
    }

    let ignore = false;

    const loadUserRecord = async () => {
      try {
        const record = await refreshUserRecord();
        if (ignore) return;
        if (record) {
          const nextName =
            (typeof record?.name === 'string' && record.name) ||
            (typeof record?.username === 'string' && record.username) ||
            (typeof record?.email === 'string' && record.email) ||
            '';
          setProfileName(nextName);
          setProfileNameBaseline(nextName);
          const nextPhone = record?.phoneNumber || record?.phone_number || '';
          setProfilePhone(nextPhone);
          setProfilePhoneBaseline(nextPhone);
          const nextLocation =
            (typeof record?.baseOperacion === 'string' && record.baseOperacion) ||
            (typeof record?.base_operacion === 'string' && record.base_operacion) ||
            '';
          setProfileLocation(nextLocation);
          setProfileLocationBaseline(nextLocation);
          const nextAbout =
            (typeof record?.aboutMe === 'string' && record.aboutMe) ||
            (typeof record?.about_me === 'string' && record.about_me) ||
            '';
          setProfileAbout(nextAbout);
          setProfileAboutBaseline(nextAbout);
          setProfileAboutError('');
          setProfileAboutSuccess('');
          setProfileBasicsError('');
          setProfileBasicsSuccess('');
          return;
        }
      } catch {
        // snapshot fallback below
      }
      if (ignore) return;
      setProfileUserRecord(currentUser);
      const nextName =
        (typeof currentUser?.name === 'string' && currentUser.name) ||
        (typeof currentUser?.username === 'string' && currentUser.username) ||
        (typeof currentUser?.email === 'string' && currentUser.email) ||
        '';
      setProfileName(nextName);
      setProfileNameBaseline(nextName);
      const nextPhone = currentUser?.phoneNumber || '';
      setProfilePhone(nextPhone);
      setProfilePhoneBaseline(nextPhone);
      const nextLocation =
        (typeof currentUser?.baseOperacion === 'string' && currentUser.baseOperacion) || '';
      setProfileLocation(nextLocation);
      setProfileLocationBaseline(nextLocation);
      const nextAbout =
        (typeof currentUser?.aboutMe === 'string' && currentUser.aboutMe) ||
        (typeof currentUser?.about_me === 'string' && currentUser.about_me) ||
        '';
      setProfileAbout(nextAbout);
      setProfileAboutBaseline(nextAbout);
    };

    loadUserRecord();
    return () => {
      ignore = true;
    };
  }, [currentUser?.id, currentUser?.updated, currentUser, refreshUserRecord]);

  const handleProfileAboutChange = value => {
    setProfileAbout(value);
    if (profileAboutError) setProfileAboutError('');
    if (profileAboutSuccess) setProfileAboutSuccess('');
  };

  const handleProfileAboutSubmit = async () => {
    if (!currentUser?.id) return;
    const normalized = (profileAbout ?? '').trim();
    const original = (profileAboutBaseline ?? '').trim();
    if (normalized === original) {
      setProfileAbout(normalized);
      setProfileAboutBaseline(original);
      setProfileAboutSuccess('Sin cambios.');
      return;
    }
    setSavingAbout(true);
    setProfileAboutError('');
    setProfileAboutSuccess('');
    try {
      const updated = await updateUserAboutMe(normalized);
      setProfileUserRecord(updated);
      const updatedAbout =
        (typeof updated?.aboutMe === 'string' && updated.aboutMe) ||
        (typeof updated?.about_me === 'string' && updated.about_me) ||
        '';
      setProfileAbout(updatedAbout);
      setProfileAboutBaseline(updatedAbout);
      setProfileAboutSuccess('Acerca de mi actualizado.');
    } catch (error) {
      setProfileAboutError(
        error?.message || 'No se pudo actualizar Acerca de mi.'
      );
    } finally {
      setSavingAbout(false);
    }
  };

  const resetProfileAbout = () => {
    setProfileAbout(profileAboutBaseline || '');
    setProfileAboutError('');
    setProfileAboutSuccess('');
  };

  const handleProfileBasicsFieldChange = (field, value) => {
    const nextValue = typeof value === 'string' ? value : '';
    if (profileBasicsError) setProfileBasicsError('');
    if (profileBasicsSuccess) setProfileBasicsSuccess('');

    switch (field) {
      case 'name':
        setProfileName(nextValue);
        break;
      case 'phoneNumber':
        setProfilePhone(nextValue);
        break;
      case 'baseOperacion':
        setProfileLocation(nextValue);
        break;
      default:
        break;
    }
  };

  const resetProfileBasics = () => {
    setProfileName(profileNameBaseline || '');
    setProfilePhone(profilePhoneBaseline || '');
    setProfileLocation(profileLocationBaseline || '');
    setProfileBasicsError('');
    setProfileBasicsSuccess('');
  };

  const handleProfileBasicsSubmit = async () => {
    if (!currentUser?.id) return;

    const normalizedName = (profileName ?? '').trim();
    const baselineName = (profileNameBaseline ?? '').trim();
    const normalizedPhone = (profilePhone ?? '').trim();
    const baselinePhone = (profilePhoneBaseline ?? '').trim();
    const normalizedLocation = (profileLocation ?? '').trim();
    const baselineLocation = (profileLocationBaseline ?? '').trim();

    const updates = {};
    if (normalizedName !== baselineName) {
      updates.name = normalizedName;
    }
    if (normalizedPhone !== baselinePhone) {
      updates.phoneNumber = normalizedPhone;
    }
    if (normalizedLocation !== baselineLocation) {
      updates.baseOperacion = normalizedLocation;
    }

    if (!Object.keys(updates).length) {
      setProfileBasicsSuccess('Sin cambios.');
      return;
    }

    setSavingBasics(true);
    setProfileBasicsError('');
    setProfileBasicsSuccess('');

    try {
      const updated = await updateUserProfileBasics(updates);
      setProfileUserRecord(updated);
      const updatedName =
        (typeof updated?.name === 'string' && updated.name) ||
        (typeof updated?.username === 'string' && updated.username) ||
        (typeof updated?.email === 'string' && updated.email) ||
        '';
      setProfileName(updatedName);
      setProfileNameBaseline(updatedName);

      const updatedPhone = updated?.phoneNumber || '';
      setProfilePhone(updatedPhone);
      setProfilePhoneBaseline(updatedPhone);

      const updatedLocation =
        (typeof updated?.baseOperacion === 'string' && updated.baseOperacion) || '';
      setProfileLocation(updatedLocation);
      setProfileLocationBaseline(updatedLocation);

      setProfileBasicsSuccess('Perfil actualizado.');
    } catch (error) {
      setProfileBasicsError(error?.message || 'No se pudo actualizar el perfil.');
    } finally {
      setSavingBasics(false);
    }
  };

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const avatarPreviewRef = useRef('');

  const clearAvatarPreview = () => {
    if (avatarPreviewRef.current) {
      URL.revokeObjectURL(avatarPreviewRef.current);
      avatarPreviewRef.current = '';
    }
    setAvatarPreview('');
  };

  const handleAvatarSelect = async file => {
    setAvatarError('');
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarError('Selecciona una imagen valida.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('La imagen debe pesar maximo 5 MB.');
      return;
    }

    if (avatarPreviewRef.current) {
      URL.revokeObjectURL(avatarPreviewRef.current);
      avatarPreviewRef.current = '';
    }
    const preview = URL.createObjectURL(file);
    avatarPreviewRef.current = preview;
    setAvatarPreview(preview);

    setUploadingAvatar(true);
    try {
      await updateUserAvatar(file);
      await refreshUserRecord();
    } catch (error) {
      setAvatarError(error?.message || 'No se pudo actualizar la foto.');
      clearAvatarPreview();
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    return () => {
      clearAvatarPreview();
    };
  }, []);

  useEffect(() => {
    const activeAvatar = profileUserRecord?.avatar || currentUser?.avatar;
    if (!activeAvatar) return;
    if (!avatarPreviewRef.current) return;
    clearAvatarPreview();
  }, [profileUserRecord?.avatar, currentUser?.avatar]);

  const profileAvatarUrl = useMemo(() => {
    if (avatarPreview) return avatarPreview;
    return buildUserAvatarUrl(profileUserRecord || currentUser, '200x200');
  }, [avatarPreview, profileUserRecord, currentUser]);

  useEffect(() => {
    const onClickOutside = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        // reservado para cerrar el menu en moviles
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return {
    section,
    setSection,
    menuRef,
    empresaId,
    // Empresa
    empresaValues,
    empresaUsers,
    empresaProfileViews,
    empresaProfileViewsTotal,
    empresaUserRequests,
    empresaLoading,
    empresaUploading,
    empresaError,
    handleEmpresaFieldChange,
    handleEmpresaSubmit,
    handleApproveJoinRequest,
    handleRejectJoinRequest,
    canEditCompany,
    canManageCompanyUsers,
    userProfileViews,
    userProfileViewsTotal,
    publicationViewDetails,
    publicationViewTotal,
    // Perfil
    profileUser: profileUserRecord || currentUser,
    profileName,
    profileNameBaseline,
    profilePhone,
    profilePhoneBaseline,
    profileLocation,
    profileLocationBaseline,
    savingBasics,
    profileBasicsError,
    profileBasicsSuccess,
    profileAbout,
    profileAboutBaseline,
    savingAbout,
    profileAboutError,
    profileAboutSuccess,
    handleProfileBasicsFieldChange,
    handleProfileBasicsSubmit,
    resetProfileBasics,
    handleProfileAboutChange,
    handleProfileAboutSubmit,
    resetProfileAbout,
    profileHeroUrl,
    handleHeroSelect,
    handleHeroRemove,
    uploadingHero,
    heroError,
    profileAvatarUrl,
    handleAvatarSelect,
    uploadingAvatar,
    avatarError,
    isAdmin: userRole === 'admin',
    isClient: userRole === 'client',
  };
}
